import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from '../../action/logout-action';
import moment from 'moment';
import {
  clearSharedProjectDetailsCache,
  getSharedProjectDetailsResponse,
} from './shared-project-details-fetch';
import { isMilestoneType, MilestoneType } from '../../../virtco/components/project/createProject/enums';

interface MilestoneFile {
  id: string;
  name: string;
  size: number;
  url: string;
  type: string;
  entityId?: string; // entityId from API response
}

interface Milestone {
  id: string;
  title: string;
  description: string;
  paymentAmount: number;
  dueDate: string | null; // Store as YYYY-MM-DD format (same as startDate/endDate) to avoid timezone issues
  files: MilestoneFile[];
}

interface SecondStepState {
  id: string | null;
  startDate: string | null; // Store as YYYY-MM-DD format to avoid timezone issues
  endDate: string | null; // Store as YYYY-MM-DD format to avoid timezone issues
  milestoneType: string; // "single" or "multiple" or ""
  totalAmount: number;
  singleMilestoneAmount: number;
  multipleMilestones: Milestone[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  fetchingId: string | null;
}

const initialState: SecondStepState = {
  id: null,
  startDate: null,
  endDate: null,
  milestoneType: '',
  totalAmount: 0,
  singleMilestoneAmount: 0,
  multipleMilestones: [],
  status: 'idle',
  fetchingId: null,
};

export const fetchProjectSecondStep = createAsyncThunk(
  'secondStep/fetchProjectSecondStep',
  async (arg: string | { projectId: string; force?: boolean }) => {
    const projectId = typeof arg === 'string' ? arg : arg.projectId;
    const force = typeof arg === 'string' ? false : !!arg.force;
    const responseData = await getSharedProjectDetailsResponse(projectId, { force });
    const projectDto = responseData?.projectDto || responseData?.project || responseData;
    
    // Handle both milestoneList (from PUT response) and projectMilestoneDtos (from GET response)
    let milestonesDto = responseData?.projectMilestoneDtos || responseData?.milestonesDto || [];
    if (!milestonesDto.length && responseData?.milestoneList) {
      // Convert milestoneList format to milestonesDto format
      milestonesDto = responseData.milestoneList.map((item: any) => ({
        ...item.milestone,
        projectImagesUrls: item.projectMediaFiles || []
      }));
    }
    
    
    // Parse startDate and endDate (store as YYYY-MM-DD format to avoid timezone issues)
    let startDate: string | null = null;
    let endDate: string | null = null;
    if (projectDto?.startDate) {
      // Extract date part from ISO string or parse as local date
      const dateStr = projectDto.startDate;
      let datePart = dateStr;
      if (dateStr.includes('T') || dateStr.includes('Z')) {
        // Extract YYYY-MM-DD from ISO string
        const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
        if (match) {
          datePart = match[1];
        }
      }
      // Parse as local date and format as YYYY-MM-DD (no timezone conversion)
      const momentDate = moment(datePart, 'YYYY-MM-DD', true);
      startDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
    }
    const parseProjectDateField = (dateStr: string | null | undefined): string | null => {
      if (!dateStr) return null;
      let datePart = dateStr;
      if (dateStr.includes('T') || dateStr.includes('Z')) {
        const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
        if (match) {
          datePart = match[1];
        }
      }
      const momentDate = moment(datePart, 'YYYY-MM-DD', true);
      return momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
    };

    if (projectDto?.endDate) {
      endDate = parseProjectDateField(projectDto.endDate);
    }
    if (!endDate && projectDto?.expectedDate) {
      endDate = parseProjectDateField(projectDto.expectedDate);
    }
    
    // Determine milestone type and parse milestones
    // Check milestone types, not just count, to handle mixed types (1 single + N multi)
    let milestoneType = '';
    let singleMilestoneAmount = 0;
    const multipleMilestones: Milestone[] = [];
    
    if (Array.isArray(milestonesDto) && milestonesDto.length > 0) {
      // Check if there's a single milestone (API: "Single" | "single" | 2)
      // If there's a single milestone, that's the active type, but preserve all milestones
      // IMPORTANT: Check both nested structure (m.milestone.type) and direct structure (m.type)
      const singleMilestone = milestonesDto.find((m: any) => {
        // Handle nested structure: { milestone: { type: "Single", ... } }
        const milestone = m.milestone || m;
        return isMilestoneType(milestone.type, MilestoneType.Single);
      });
      
      // IMPORTANT: If Single milestone exists, set type to 'single' and preserve ALL milestones
      // This handles mixed types (1 Single + N Multiple) - Single takes precedence
      // NOTE: Don't check milestonesDto.length === 1 because a single Multiple milestone should still be treated as 'multi'
      if (singleMilestone) {
        milestoneType = 'single';
        
        // Find the single milestone (either the found one or the first one if length === 1)
        const singleMilestoneData = singleMilestone || milestonesDto[0];
        const singleMilestoneObj = singleMilestoneData.milestone || singleMilestoneData;
        const milestoneFiles: MilestoneFile[] = [];
        
        // Handle projectImagesUrls, projectMediaFiles (from milestoneList), and milestoneMediaFiles (from projectMilestoneDtos)
        const projectImagesUrls = singleMilestoneObj?.projectImagesUrls || 
                                  singleMilestoneData?.projectMediaFiles || 
                                  singleMilestoneData?.milestoneMediaFiles || [];
        
        if (Array.isArray(projectImagesUrls) && projectImagesUrls.length > 0) {
          projectImagesUrls
            .filter((img: any) => !img.isDeleted)
            .forEach((img: any) => {
              const fileUrl = img.imageUrl || img.filePath || img.url || img.fileUrl || '';
              milestoneFiles.push({
                id: img.id || '',
                name: img.fileName || img.name || 'file',
                size: img.fileSize || img.size || 0,
                url: fileUrl,
                type: img.mimeType || img.type || 'application/octet-stream',
                entityId: img.entityId || undefined // Store entityId from API response
              });
            });
        }
        
        // Convert dueDate to YYYY-MM-DD format (same as startDate/endDate) to avoid timezone issues
        // Handle both endDate and enddate (case variations)
        let dueDate: string | null = null;
        if (singleMilestoneObj.endDate) {
          const dateStr = singleMilestoneObj.endDate;
          let datePart = dateStr;
          if (dateStr.includes('T') || dateStr.includes('Z')) {
            const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
            if (match) {
              datePart = match[1];
            }
          }
          const momentDate = moment(datePart, 'YYYY-MM-DD', true);
          dueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
        } else if (singleMilestoneObj.enddate) {
          const dateStr = singleMilestoneObj.enddate;
          let datePart = dateStr;
          if (dateStr.includes('T') || dateStr.includes('Z')) {
            const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
            if (match) {
              datePart = match[1];
            }
          }
          const momentDate = moment(datePart, 'YYYY-MM-DD', true);
          dueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
        } else if (singleMilestoneObj.startDate) {
          const dateStr = singleMilestoneObj.startDate;
          let datePart = dateStr;
          if (dateStr.includes('T') || dateStr.includes('Z')) {
            const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
            if (match) {
              datePart = match[1];
            }
          }
          const momentDate = moment(datePart, 'YYYY-MM-DD', true);
          dueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
        } else if (singleMilestoneObj.startdate) {
          const dateStr = singleMilestoneObj.startdate;
          let datePart = dateStr;
          if (dateStr.includes('T') || dateStr.includes('Z')) {
            const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
            if (match) {
              datePart = match[1];
            }
          }
          const momentDate = moment(datePart, 'YYYY-MM-DD', true);
          dueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
        } else if (endDate) {
          dueDate = endDate;
        }
        
        // Get milestone ID - handle both direct milestone object and nested milestone object
        // Same pattern as multi-milestone (line 164) to ensure consistency
        const milestoneId = singleMilestoneObj.id || singleMilestoneObj.milestone?.id || '';
        
        // Get amount - handle both direct and nested (same pattern as multi-milestone)
        const milestoneAmount = singleMilestoneObj.amount || singleMilestoneObj.milestone?.amount || 0;
        singleMilestoneAmount = milestoneAmount;
        
        // Store single milestone ID in multipleMilestones[0] for retrieval during updates
        // This ID will be used in buildMilestonePayload to determine isDeleted flag
        multipleMilestones.push({
          id: milestoneId,
          title: singleMilestoneObj.title || singleMilestoneObj.milestone?.title || '',
          description: singleMilestoneObj.description || singleMilestoneObj.milestone?.description || '',
          paymentAmount: milestoneAmount,
          dueDate: dueDate as string | null, // Store as ISO string for Redux persist
          files: milestoneFiles
        } as Milestone);
        
        // IMPORTANT: Also store all other milestones (multi milestones) so they can be preserved
        // This handles the case where API returns mixed types (1 single + N multi)
        // NOTE: We preserve ALL milestones (including deleted ones) for originalData tracking
        // Deleted milestones will be filtered out in the component when passing to UI
        milestonesDto.forEach((milestone: any) => {
          const milestoneObj = milestone.milestone || milestone;
          // Skip the single milestone we already added
          if (milestoneObj.id === milestoneId) return;
          
          // IMPORTANT: Preserve ALL milestones (including deleted ones) for payload generation
          // Deleted milestones will be filtered out in the component when passing to UI
          // This ensures they can be marked as deleted in payload when switching types
          
          // Only add milestones with valid UUIDs (from API)
          const otherMilestoneId = milestoneObj.id || milestoneObj.milestone?.id || '';
          if (!otherMilestoneId || otherMilestoneId === "00000000-0000-0000-0000-000000000000") return;
          
          const otherMilestoneFiles: MilestoneFile[] = [];
          // Handle projectImagesUrls, projectMediaFiles (from milestoneList), and milestoneMediaFiles (from projectMilestoneDtos)
          const otherProjectImagesUrls = milestoneObj?.projectImagesUrls || 
                                        milestone?.projectMediaFiles || 
                                        milestone?.milestoneMediaFiles || [];
          
          if (Array.isArray(otherProjectImagesUrls) && otherProjectImagesUrls.length > 0) {
            otherProjectImagesUrls
              .filter((img: any) => !img.isDeleted)
              .forEach((img: any) => {
                const fileUrl = img.imageUrl || img.filePath || img.url || img.fileUrl || '';
                otherMilestoneFiles.push({
                  id: img.id || '',
                  name: img.fileName || img.name || 'file',
                  size: img.fileSize || img.size || 0,
                  url: fileUrl,
                  type: img.mimeType || img.type || 'application/octet-stream',
                  entityId: img.entityId || undefined // Store entityId from API response
                });
              });
          }
          
          // Convert dueDate to YYYY-MM-DD format (same as startDate/endDate) to avoid timezone issues
          let otherDueDate: string | null = null;
          if (milestoneObj.endDate) {
            const dateStr = milestoneObj.endDate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            otherDueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          } else if (milestoneObj.enddate) {
            const dateStr = milestoneObj.enddate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            otherDueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          } else if (milestoneObj.startDate) {
            const dateStr = milestoneObj.startDate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            otherDueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          } else if (milestoneObj.startdate) {
            const dateStr = milestoneObj.startdate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            otherDueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          }
          
          // Add other milestones (multi milestones) to preserve them for deletion in payload
          // Include deleted milestones so they can be tracked in originalData
          multipleMilestones.push({
            id: otherMilestoneId,
            title: milestoneObj.title || milestoneObj.milestone?.title || '',
            description: milestoneObj.description || milestoneObj.milestone?.description || '',
            paymentAmount: milestoneObj.amount || milestoneObj.milestone?.amount || 0,
            dueDate: otherDueDate as string | null,
            files: otherMilestoneFiles,
            isDeleted: milestoneObj.isDeleted === true || milestone.isDeleted === true // Preserve isDeleted flag
          } as Milestone & { isDeleted?: boolean });
        });
      } else {
        // Check again for Single milestone - this should have been caught above, but double-check
        // This handles edge cases where Single milestone detection might have failed
        const singleMilestoneRecheck = milestonesDto.find((m: any) => {
          const milestone = m.milestone || m;
          return isMilestoneType(milestone.type, MilestoneType.Single);
        });
        
        if (singleMilestoneRecheck) {
          // Single milestone exists but wasn't caught in first check - this shouldn't happen, but handle it
          milestoneType = 'single';
          // Process as single (will be handled by the logic above in next iteration or we need to process it here)
          // For now, set to 'single' and let the component handle it
        } else {
          // Check if any milestone has Multiple type (API: "Multiple" | "multi" | 1)
          const hasMultipleType = milestonesDto.some((m: any) => {
            const milestone = m.milestone || m;
            return isMilestoneType(milestone.type, MilestoneType.Multiple);
          });
          milestoneType = hasMultipleType ? 'multi' : (milestonesDto.length > 1 ? 'multi' : ''); // Set to 'multi' if multiple milestones exist
        }
        
        // IMPORTANT: Check if there's a Single milestone in the response even when type is 'multiple'
        // This handles the case where API returns mixed types (1 Single + N Multiple)
        // and user is currently on multiple-milestone type
        const singleMilestoneInResponse = milestonesDto.find((m: any) => {
          const milestone = m.milestone || m;
          return isMilestoneType(milestone.type, MilestoneType.Single);
        });
        
        // If Single milestone exists, preserve it first
        if (singleMilestoneInResponse) {
          const singleMilestoneData = singleMilestoneInResponse;
          const singleMilestoneObj = singleMilestoneData.milestone || singleMilestoneData;
          const singleMilestoneFiles: MilestoneFile[] = [];
          
          // Handle projectImagesUrls, projectMediaFiles (from milestoneList), and milestoneMediaFiles (from projectMilestoneDtos)
          const singleProjectImagesUrls = singleMilestoneObj?.projectImagesUrls || 
                                        singleMilestoneData?.projectMediaFiles || 
                                        singleMilestoneData?.milestoneMediaFiles || [];
          
          if (Array.isArray(singleProjectImagesUrls) && singleProjectImagesUrls.length > 0) {
            singleProjectImagesUrls
              .filter((img: any) => !img.isDeleted)
              .forEach((img: any) => {
                const fileUrl = img.imageUrl || img.filePath || img.url || img.fileUrl || '';
                singleMilestoneFiles.push({
                  id: img.id || '',
                  name: img.fileName || img.name || 'file',
                  size: img.fileSize || img.size || 0,
                  url: fileUrl,
                  type: img.mimeType || img.type || 'application/octet-stream',
                  entityId: img.entityId || undefined
                });
              });
          }
          
          // Convert dueDate to YYYY-MM-DD format (same as startDate/endDate) to avoid timezone issues
          let singleDueDate: string | null = null;
          if (singleMilestoneObj.endDate) {
            const dateStr = singleMilestoneObj.endDate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            singleDueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          } else if (singleMilestoneObj.enddate) {
            const dateStr = singleMilestoneObj.enddate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            singleDueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          } else if (singleMilestoneObj.startDate) {
            const dateStr = singleMilestoneObj.startDate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            singleDueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          } else if (singleMilestoneObj.startdate) {
            const dateStr = singleMilestoneObj.startdate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            singleDueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          }
          
          const singleMilestoneId = singleMilestoneObj.id || singleMilestoneObj.milestone?.id || '';
          const singleMilestoneAmountValue = singleMilestoneObj.amount || singleMilestoneObj.milestone?.amount || 0;
          singleMilestoneAmount = singleMilestoneAmountValue; // Store for later use
          
          // Preserve Single milestone in multipleMilestones array (will be filtered out in UI but preserved for payload)
          multipleMilestones.push({
            id: singleMilestoneId,
            title: singleMilestoneObj.title || singleMilestoneObj.milestone?.title || '',
            description: singleMilestoneObj.description || singleMilestoneObj.milestone?.description || '',
            paymentAmount: singleMilestoneAmountValue,
            dueDate: singleDueDate as string | null,
            files: singleMilestoneFiles
          } as Milestone);
        }
        
        milestonesDto.forEach((milestone: any, index: number) => {
          const milestoneObj = milestone.milestone || milestone;
          
          // Skip Single milestone if we already processed it above
          if (singleMilestoneInResponse && (milestone === singleMilestoneInResponse || milestoneObj.id === (singleMilestoneInResponse.milestone || singleMilestoneInResponse).id)) {
            return;
          }
          
          // IMPORTANT: Preserve ALL milestones (including deleted ones) for originalData tracking
          // Deleted milestones will be filtered out in the component when passing to UI
          // This ensures they can be marked as deleted in payload when switching types
          
          const milestoneFiles: MilestoneFile[] = [];
          // Handle projectImagesUrls, projectMediaFiles (from milestoneList), and milestoneMediaFiles (from projectMilestoneDtos)
          // IMPORTANT: milestoneMediaFiles is at the top level of the milestone item, not inside milestone.milestone
          const projectImagesUrls = milestoneObj?.projectImagesUrls || 
                                    milestone?.projectMediaFiles || 
                                    milestone?.milestoneMediaFiles || 
                                    (milestone && 'milestoneMediaFiles' in milestone ? milestone.milestoneMediaFiles : []) || [];
          
          if (Array.isArray(projectImagesUrls) && projectImagesUrls.length > 0) {
            projectImagesUrls
              .filter((img: any) => !img.isDeleted)
              .forEach((img: any) => {
                const fileUrl = img.imageUrl || img.filePath || img.url || img.fileUrl || '';
                milestoneFiles.push({
                  id: img.id || '',
                  name: img.fileName || img.name || 'file',
                  size: img.fileSize || img.size || 0,
                  url: fileUrl,
                  type: img.mimeType || img.type || 'application/octet-stream',
                  entityId: img.entityId || undefined // Store entityId from API response
                });
              });
          }
          
          // Convert dueDate to YYYY-MM-DD format (same as startDate/endDate) to avoid timezone issues
          // Handle both endDate and enddate (case variations)
          let dueDate: string | null = null;
          if (milestoneObj.endDate) {
            const dateStr = milestoneObj.endDate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            dueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          } else if (milestoneObj.enddate) {
            const dateStr = milestoneObj.enddate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            dueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          } else if (milestoneObj.startDate) {
            const dateStr = milestoneObj.startDate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            dueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          } else if (milestoneObj.startdate) {
            const dateStr = milestoneObj.startdate;
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            dueDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
          }
          
          // Get milestone ID - handle both direct milestone object and nested milestone object
          const milestoneId = milestoneObj.id || milestone.milestone?.id || '';
          
          // Only add milestone if it has a valid ID
          if (milestoneId && milestoneId !== "00000000-0000-0000-0000-000000000000") {
            multipleMilestones.push({
              id: milestoneId,
              title: milestoneObj.title || milestone.milestone?.title || '',
              description: milestoneObj.description || milestone.milestone?.description || '',
              paymentAmount: milestoneObj.amount || milestoneObj.milestone?.amount || 0,
              dueDate: dueDate as string | null, // Store as ISO string for Redux persist
              files: milestoneFiles,
              isDeleted: milestoneObj.isDeleted === true || milestone.isDeleted === true // Preserve isDeleted flag
            } as Milestone & { isDeleted?: boolean });
          }
        });
      }
    }
    
    // Calculate total amount
    const totalAmount = milestoneType === 'single' 
      ? singleMilestoneAmount 
      : multipleMilestones.reduce((sum, m) => sum + (m.paymentAmount || 0), 0);
    
    
    return {
      id: projectDto?.id || null,
      startDate,
      endDate,
      milestoneType,
      totalAmount,
      singleMilestoneAmount,
      multipleMilestones,
    };
  }
);

const secondStepSlice = createSlice({
  name: 'secondStep',
  initialState,
  reducers: {
    setSecondStepData: (state, action: PayloadAction<Partial<SecondStepState>>) => {
      return { ...state, ...action.payload };
    },
    clearData(state) {
      state.id = null;
      state.startDate = null;
      state.endDate = null;
      state.milestoneType = '';
      state.totalAmount = 0;
      state.singleMilestoneAmount = 0;
      state.multipleMilestones = [];
      state.status = 'idle';
      // Block in-flight responses until a new pending sets the real project id
      state.fetchingId = '';
      clearSharedProjectDetailsCache();
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchProjectSecondStep.pending, (state, action) => {
        state.status = 'loading';
        state.fetchingId = typeof action.meta.arg === 'string' ? action.meta.arg : action.meta.arg.projectId;
      })
      .addCase(fetchProjectSecondStep.fulfilled, (state, action) => {
        const requestId = typeof action.meta.arg === 'string' ? action.meta.arg : action.meta.arg.projectId;
        // '' = clearData (block late responses); other id = newer request owns the slot
        if (state.fetchingId === '' || (state.fetchingId != null && state.fetchingId !== requestId)) {
          return;
        }
        state.status = 'succeeded';
        state.id = action.payload.id;
        if (action.payload.startDate) {
          state.startDate = action.payload.startDate;
        }
        if (action.payload.endDate) {
          state.endDate = action.payload.endDate;
        }
        const incomingMilestones = action.payload.multipleMilestones || [];
        const hasSavedMilestoneRows = (state.multipleMilestones || []).some(
          (m) => !!(m.title && m.title.trim()) || (m.paymentAmount || 0) > 0 || !!m.dueDate
        );
        if (action.payload.milestoneType) {
          state.milestoneType = action.payload.milestoneType;
        } else if (incomingMilestones.length > 0) {
          state.milestoneType = state.milestoneType || 'multi';
        } else if (hasSavedMilestoneRows) {
          // Empty API vs leftover saved rows (draft persist). Do not wipe a type
          // the user just picked on an empty form (first Multi/Single click).
          state.milestoneType = '';
        }
        if (typeof action.payload.totalAmount === 'number') {
          state.totalAmount = action.payload.totalAmount;
        }
        const incomingSingleAmount = action.payload.singleMilestoneAmount || 0;
        const amountFromMilestone = incomingMilestones[0]?.paymentAmount || 0;
        const resolvedSingleAmount = incomingSingleAmount > 0 ? incomingSingleAmount : amountFromMilestone;
        if (resolvedSingleAmount > 0) {
          state.singleMilestoneAmount = resolvedSingleAmount;
        } else if (!(state.milestoneType === 'single' && (state.singleMilestoneAmount || 0) > 0)) {
          state.singleMilestoneAmount = 0;
        }
        if (incomingMilestones.length > 0) {
          state.multipleMilestones = incomingMilestones.map(m => {
            if (!m.dueDate) return { ...m, dueDate: null };

            if (typeof m.dueDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(m.dueDate)) {
              return { ...m, dueDate: m.dueDate };
            }

            const dateStr = typeof m.dueDate === 'string' ? m.dueDate : new Date(m.dueDate).toISOString();
            let datePart = dateStr;
            if (dateStr.includes('T') || dateStr.includes('Z')) {
              const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
              if (match) {
                datePart = match[1];
              }
            }
            const momentDate = moment(datePart, 'YYYY-MM-DD', true);
            return {
              ...m,
              dueDate: momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null
            };
          });
        } else if (hasSavedMilestoneRows) {
          state.multipleMilestones = [];
        }
        state.fetchingId = null;
      })
      .addCase(fetchProjectSecondStep.rejected, (state, action) => {
        const requestId = typeof action.meta.arg === 'string' ? action.meta.arg : action.meta.arg.projectId;
        if (state.fetchingId !== requestId) {
          return;
        }
        state.status = 'failed';
        state.fetchingId = null;
      })
      .addCase(logout, () => initialState);
  },
});

export const { setSecondStepData, clearData } = secondStepSlice.actions;
export default secondStepSlice.reducer;

// Selectors
export const selectSecondStepStatus = (state: any) => state.secondStep.status;

